import React, { useEffect, useMemo, useState } from 'react';
import { fetchHotelPrices, fetchHotels, HotelsPagination } from '../../api/Hotels';
import { setAdminHotelFeatured } from '../../api/HotelAdmin';
import { Hotel } from '../../types/Quotation';

type PackagePlace = { cityName: string; cityCode: string; nights: number };
type Sort = 'rating-desc' | 'rating-asc' | 'price-asc' | 'price-desc';

type Props = { places: PackagePlace[]; checkIn: string };
type FeaturedHotel = Hotel & { is_featured?: boolean };

const hotelRef = (hotel: Hotel) => hotel.hotelRef || hotel.hotel_ref || `${hotel.provider}:${hotel.hotelId}`;
const hotelPrice = (hotel: Hotel) => Number(hotel.display_price ?? hotel.price ?? 0);

const priceFromInfo = (priceInfo: any): number | undefined => {
  const variants = priceInfo?.roomVariants ?? priceInfo?.variants ?? priceInfo?.rooms;
  if (Array.isArray(variants)) {
    const prices = variants.map((variant) => Number(variant?.price)).filter(Number.isFinite);
    if (prices.length) return Math.min(...prices);
  }
  const direct = Number(priceInfo?.price ?? priceInfo?.display_price ?? priceInfo?.displayPrice);
  return Number.isFinite(direct) ? direct : undefined;
};

export const applyFeaturedHotelPrices = (hotels: FeaturedHotel[], prices: Record<string, any>): FeaturedHotel[] => hotels.map((hotel) => {
  const keys = [hotel.hotelRef, hotel.hotel_ref, hotel.hotelId, hotel.providerHotelId].filter(Boolean).map(String);
  const priceInfo = keys.map((key) => prices[key]).find(Boolean);
  const price = priceFromInfo(priceInfo);
  if (price === undefined) return hotel;
  const variants = priceInfo?.roomVariants ?? priceInfo?.variants ?? priceInfo?.rooms;
  const currency = priceInfo?.currency ?? priceInfo?.display_currency ?? variants?.[0]?.currency ?? hotel.currency;
  return { ...hotel, price, display_price: price, ...(currency ? { currency, display_currency: currency } : {}), ...(Array.isArray(variants) ? { roomVariants: variants } : {}) };
});

export default function FeaturedHotelsSection({ places, checkIn }: Props) {
  const packagePlaces = useMemo(() => places.filter((place) => place.cityName && place.cityCode), [places]);
  const [placeIndex, setPlaceIndex] = useState(0);
  const [hotelName, setHotelName] = useState('');
  const [starRating, setStarRating] = useState(0);
  const [sort, setSort] = useState<Sort>('rating-desc');
  const [items, setItems] = useState<FeaturedHotel[]>([]);
  const [pagination, setPagination] = useState<HotelsPagination | undefined>();
  const [priceJobId, setPriceJobId] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingRef, setUpdatingRef] = useState('');
  const [error, setError] = useState('');

  const selectedPlace = packagePlaces[placeIndex];
  const sortedItems = useMemo(() => [...items].sort((left, right) => {
    if (sort === 'rating-desc') return Number(right.starRating) - Number(left.starRating);
    if (sort === 'rating-asc') return Number(left.starRating) - Number(right.starRating);
    if (sort === 'price-asc') return hotelPrice(left) - hotelPrice(right);
    return hotelPrice(right) - hotelPrice(left);
  }), [items, sort]);

  const search = async (page = 1) => {
    if (!selectedPlace) return;
    setLoading(true);
    setPriceJobId('');
    setError('');
    try {
      const result = await fetchHotels({
        city: selectedPlace.cityCode,
        checkIn,
        checkOut: new Date(new Date(`${checkIn}T00:00:00Z`).getTime() + Math.max(1, selectedPlace.nights) * 86_400_000).toISOString().slice(0, 10),
        rooms: [{ Adults: 2, Children: 0, ChildrenAges: null }],
        filters: { HotelName: hotelName.trim() || undefined, StarRating: starRating || undefined },
        page,
        limit: 20,
      });
      setItems(result.items as FeaturedHotel[]);
      setPagination(result.pagination);
      setPriceJobId(result.priceJobId ?? result.price_job_id ?? '');
    } catch (searchError: any) {
      setItems([]);
      setPagination(undefined);
      setError(searchError?.message || 'Unable to search hotels.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!priceJobId) return;
    let cancelled = false;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      attempts += 1;
      try {
        const response = await fetchHotelPrices(priceJobId);
        if (cancelled) return;
        if (response?.prices && typeof response.prices === 'object') {
          setItems((current) => applyFeaturedHotelPrices(current, response.prices as Record<string, any>));
        }
        if (response?.status === 'DONE' || response?.status === 'FAILED') return;
      } catch {
        // Keep polling briefly; the asynchronous supplier job may not be visible yet.
      }
      if (!cancelled && attempts < 30) timeoutId = setTimeout(poll, 1500);
    };

    void poll();
    return () => { cancelled = true; if (timeoutId) clearTimeout(timeoutId); };
  }, [priceJobId]);

  const updateFeatured = async (hotel: FeaturedHotel, isFeatured: boolean) => {
    const ref = hotelRef(hotel);
    setUpdatingRef(ref);
    setError('');
    try {
      await setAdminHotelFeatured(ref, isFeatured);
      setItems((current) => current.map((item) => hotelRef(item) === ref ? { ...item, is_featured: isFeatured } : item));
    } catch (updateError: any) {
      setError(updateError?.message || 'Unable to update the featured hotel.');
    } finally {
      setUpdatingRef('');
    }
  };

  if (!packagePlaces.length) return <div className="guided-package-builder__empty-state">Add package cities before selecting featured hotels.</div>;

  return <div className="guided-package-builder__stack featured-hotels">
    <div className="featured-hotels__intro">
      <div><span className="featured-hotels__eyebrow">Hotel catalogue</span><div className="guided-package-builder__help">Choose the hotels that should appear first for each package city.</div></div>
      {items.length > 0 && <span className="featured-hotels__count">{items.length} result{items.length === 1 ? '' : 's'}</span>}
    </div>
    <div className="featured-hotels__filters">
      <label><span>Package city</span><select value={placeIndex} onChange={(event) => { setPlaceIndex(Number(event.target.value)); setItems([]); setPagination(undefined); }} className="package-editor-input">{packagePlaces.map((place, index) => <option key={`${place.cityCode}-${index}`} value={index}>{place.cityName} · {place.nights} night{place.nights === 1 ? '' : 's'}</option>)}</select></label>
      <label><span>Hotel name</span><input value={hotelName} onChange={(event) => setHotelName(event.target.value)} className="package-editor-input" placeholder="Filter by hotel name" /></label>
      <label><span>Star rating</span><select value={starRating} onChange={(event) => setStarRating(Number(event.target.value))} className="package-editor-input"><option value={0}>All</option>{[5, 4, 3, 2, 1].map((star) => <option key={star} value={star}>{star} star</option>)}</select></label>
      <label><span>Sort by</span><select value={sort} onChange={(event) => setSort(event.target.value as Sort)} className="package-editor-input"><option value="rating-desc">Rating: high to low</option><option value="rating-asc">Rating: low to high</option><option value="price-asc">Price: low to high</option><option value="price-desc">Price: high to low</option></select></label>
      <button type="button" className="package-editor-btn-primary featured-hotels__search" onClick={() => void search()} disabled={loading}>{loading ? 'Searching…' : 'Search hotels'}</button>
    </div>
    {error && <div className="guided-package-builder__error">{error}</div>}
    {sortedItems.length > 0 && <div className="featured-hotels__list" aria-label="Hotel search results">
      <div className="featured-hotels__list-header" aria-hidden="true"><span>Hotel</span><span>From price</span><span /></div>
      {sortedItems.map((hotel) => {
      const ref = hotelRef(hotel);
      const featured = Boolean(hotel.is_featured);
      return <div className="featured-hotels__row" key={ref}>
        <div className="featured-hotels__details"><strong>{hotel.name}</strong><div className="featured-hotels__meta"><span>{hotel.starRating} star</span><span>{hotel.provider}</span><span title={hotel.address || selectedPlace.cityName}>{hotel.address || selectedPlace.cityName}</span></div></div>
        <div className="featured-hotels__price"><span>From</span><strong>{hotel.currency || hotel.display_currency || 'INR'} {hotelPrice(hotel).toLocaleString('en-IN')}</strong></div>
        <button type="button" className={featured ? 'package-editor-btn-ghost' : 'package-editor-btn-primary'} disabled={updatingRef === ref} onClick={() => void updateFeatured(hotel, !featured)}>{updatingRef === ref ? 'Updating…' : featured ? 'Remove' : 'Feature hotel'}</button>
      </div>;
      })}
    </div>}
    {pagination && pagination.totalPages > 1 && <div className="guided-package-builder__section-head">
      <span className="guided-package-builder__help">Page {pagination.page} of {pagination.totalPages} · {pagination.total} hotels</span>
      <div className="guided-package-builder__actions">
        <button type="button" className="package-editor-btn-ghost" disabled={loading || pagination.page <= 1} onClick={() => void search(pagination.page - 1)}>Previous</button>
        <button type="button" className="package-editor-btn-ghost" disabled={loading || pagination.page >= pagination.totalPages} onClick={() => void search(pagination.page + 1)}>Next</button>
      </div>
    </div>}
    {!loading && !error && items.length === 0 && <div className="guided-package-builder__empty-state">Search this city to choose one or more featured hotels.</div>}
  </div>;
}
